import {
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Args,
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Options,
  useRentalManagerUserPropertyMultiplexV2WebApiHandler_caption,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type {
  ApiResponseErrorDto,
  UserPropertyMultiplexPhotoResponseV2WebDto as UserPropertyMultiplexPhotoResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type { UserPropertyMultiplexPhotoResponseWebDto };

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `postData.multiplexBuildingSessionId` and `postData.photoId` must be non-empty strings.
 */
const assertMultiplexPhotoCaptionMutationArgs = (
  args: RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Args,
): void => {
  const { postData } = args;

  if (typeof postData !== 'object' || postData === null) {
    throw new TypeError(
      'useMultiplexPhotoCaption: `postData` is required (UserPropertyMultiplexPhotoCaptionV2RequestDto).',
    );
  }

  const sessionId = postData.multiplexBuildingSessionId;
  const photoId = postData.photoId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexPhotoCaption: `postData.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof photoId !== 'string' || photoId.trim() === '') {
    throw new TypeError('useMultiplexPhotoCaption: `postData.photoId` is required on every call (non-empty string).');
  }

  if (postData.caption !== undefined && postData.caption !== null && typeof postData.caption !== 'string') {
    throw new TypeError('useMultiplexPhotoCaption: when set, `postData.caption` must be a string.');
  }
};

export type UseMultiplexPhotoCaptionOptions = Omit<
  RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexPhotoCaptionMutation = ReturnType<
  typeof useRentalManagerUserPropertyMultiplexV2WebApiHandler_caption
>;

export type UseMultiplexPhotoCaptionResult = Omit<
  BaseMultiplexPhotoCaptionMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Args,
    options?: Parameters<BaseMultiplexPhotoCaptionMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexPhotoCaptionMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_caption_Args,
    options?: Parameters<BaseMultiplexPhotoCaptionMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexPhotoCaptionMutation['mutateAsync']>;
  data: BaseMultiplexPhotoCaptionMutation['data'];
  error: BaseMultiplexPhotoCaptionMutation['error'];
  isPending: BaseMultiplexPhotoCaptionMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexPhotoResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexPhotoResponseWebDto['units'] | undefined;
};

/**
 * Multiplex photo caption: wraps the generated `caption` mutation (POST `/api/web/v2/users/properties/multiplex/photos/caption`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ postData: { multiplexBuildingSessionId, photoId, caption? } }`. Compile-time typing
 * applies to `mutate` / `mutateAsync`; runtime checks enforce non-empty session id and photo id strings.
 */
const useMultiplexPhotoCaption = (
  mutationOptions: UseMultiplexPhotoCaptionOptions = {},
): UseMultiplexPhotoCaptionResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexV2WebApiHandler_caption({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'RentalManagerUserPropertyMultiplexV2WebApiHandler',
        details: 'multiplexBuildingSessionPhotoCaption',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexPhotoCaptionMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexPhotoCaptionMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexPhotoCaption;
